/**
 * ShopNameScreen — UC-04: đặt tên quán.
 *
 * 3 gợi ý nhanh để giảm friction nhập liệu.
 */
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { extractErrorMessage } from "../lib/api";
import { track } from "../lib/analytics";
import { useShopStore } from "../stores/shopStore";

// Tên quán gợi ý — giữ tiếng Việt theo bản sắc cà phê Việt.
const QUICK_SUGGESTIONS = [
  "Cà Phê Góc Nhỏ",
  "Bạc Xỉu Buổi Sáng",
  "Quán Của Tôi",
];

export function ShopNameScreen() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const setShopName = useShopStore((s) => s.setShopName);

  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    const trimmed = name.trim();
    if (trimmed.length === 0 || trimmed.length > 30) {
      setError(t("shop_name.char_count", { count: trimmed.length }));
      return;
    }
    setSubmitting(true);
    try {
      await setShopName(trimmed);
      track("shop_name_created", { name_length: trimmed.length });
      navigate("/");
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="min-h-full flex items-center justify-center p-6 bg-cream">
      <form
        onSubmit={onSubmit}
        className="bg-white rounded-2xl p-8 shadow-soft-amber border border-amber50/30 w-full max-w-sm"
      >
        <div className="text-center mb-6">
          <div className="text-5xl mb-3">🪧</div>
          <h1 className="text-xl font-bold text-coffee">{t("shop_name.title")}</h1>
        </div>

        <input
          type="text"
          maxLength={30}
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t("shop_name.placeholder")}
          disabled={submitting}
          className="w-full px-4 py-3 rounded-lg border-2 border-coffee/20 focus:border-coffee/60 outline-none text-coffee font-medium"
          data-testid="shop-name-input"
        />
        <div className="text-xs text-coffee/40 mt-1 text-right">
          {t("shop_name.char_count", { count: name.length })}
        </div>

        <div className="mt-3">
          <div className="text-xs text-coffee/60 mb-2">{t("shop_name.suggest_label")}</div>
          <div className="flex flex-wrap gap-2">
            {QUICK_SUGGESTIONS.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setName(s)}
                disabled={submitting}
                className="text-xs px-3 py-1 rounded-full bg-cream border border-coffee/15 hover:border-coffee/40 text-coffee"
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        {error && (
          <div className="mt-3 text-sm text-stool" data-testid="name-error">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="mt-5 w-full py-2.5 rounded-lg bg-coffee text-white font-semibold hover:bg-coffee/90 disabled:opacity-60"
        >
          {submitting ? t("auth.processing") : t("shop_name.save_button")}
        </button>
      </form>
    </main>
  );
}
